/**
 * Custom hook for managing product data
 */

import { useEffect, useState } from 'react';

import {
  fetchProductBySlug,
  fetchProducts,
  fetchRelatedProducts,
} from '../services/api/product';
import { Product, ProductListResponse } from '../services/types/product';

export interface UseProductsReturn {
  data: ProductListResponse;
  loading: boolean;
  error: string | null;
  refetch: (params?: {
    page?: number;
    pageSize?: number;
    isActive?: boolean;
    categorySlug?: string;
  }) => Promise<void>;
}

/**
 * Hook for fetching products with pagination and filtering
 */
export function useProducts(params?: {
  page?: number;
  pageSize?: number;
  isActive?: boolean;
  categorySlug?: string;
}): UseProductsReturn {
  const [data, setData] = useState<ProductListResponse>({
    data: [],
    meta: {
      pagination: {
        page: 1,
        pageSize: 9,
        pageCount: 0,
        total: 0,
      },
    },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async (fetchParams?: {
    page?: number;
    pageSize?: number;
    isActive?: boolean;
    categorySlug?: string;
  }) => {
    try {
      setLoading(true);
      setError(null);

      console.log('useProducts: Fetching with params:', fetchParams || params);
      const result = await fetchProducts(fetchParams || params);

      console.log('useProducts: Received data:', result);
      setData(result);
    } catch (err) {
      console.error('useProducts: Error fetching products:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch products';
      setError(errorMessage);

      // Set empty data on error
      setData({
        data: [],
        meta: {
          pagination: {
            page: 1,
            pageSize: 9,
            pageCount: 0,
            total: 0,
          },
        },
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    console.log('useProducts: Effect triggered with params:', params);
    fetchData(params);
  }, [params?.page, params?.pageSize, params?.isActive, params?.categorySlug]);

  const refetch = async (refetchParams?: {
    page?: number;
    pageSize?: number;
    isActive?: boolean;
    categorySlug?: string;
  }) => {
    console.log('useProducts: Refetching with params:', refetchParams);
    await fetchData(refetchParams);
  };

  return {
    data,
    loading,
    error,
    refetch,
  };
}

export interface UseProductDetailReturn {
  product: Product | null;
  relatedProducts: Product[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching a single product by slug
 */
export function useProductDetail(slug: string): UseProductDetailReturn {
  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('useProductDetail: Fetching product with slug:', slug);
      const productResult = await fetchProductBySlug(slug);
      setProduct(productResult.data);

      // Fetch related products
      if (productResult.data) {
        console.log('useProductDetail: Fetching related products');
        const relatedResult = await fetchRelatedProducts(
          productResult.data.id,
          3
        );
        setRelatedProducts(relatedResult.data);
      }
    } catch (err) {
      console.error('useProductDetail: Error fetching product:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch product';
      setError(errorMessage);
      setProduct(null);
      setRelatedProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (slug) {
      console.log('useProductDetail: Effect triggered with slug:', slug);
      fetchData();
    }
  }, [slug]);

  const refetch = async () => {
    console.log('useProductDetail: Refetching product');
    await fetchData();
  };

  return {
    product,
    relatedProducts,
    loading,
    error,
    refetch,
  };
}
